import { useEffect, useMemo, useState } from "react";
import {
  Clock,
  Check,
  X as XIcon,
  RefreshCcw,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";

const STATUS_TONES = {
  pending: "border-amber-500/50 text-amber-700",
  approved: "border-emerald-700/50 text-emerald-700",
  rejected: "border-rose-500/50 text-rose-700",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fmtValue = (v) => {
  if (v === null || v === undefined || v === "") {
    return <span className="italic text-[var(--ld-ink-muted)]">—</span>;
  }
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const FIELD_LABEL = {
  company_name: "Company",
  full_name: "Contact name",
  contact_number: "Phone",
  starting_price: "Starting price",
  pricing_structure: "Pricing structure",
  packages_addons: "Packages & add-ons",
  coverage: "Coverage",
  short_intro: "Short intro",
  service_description: "Service description",
  service_types: "Service types",
  based_in: "Based in",
  other_details: "Other details",
};

const ListingEditRow = ({ edit, onApprove, onReject, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const keys = Object.keys(edit.proposed || {});
  const tone = STATUS_TONES[edit.status] || STATUS_TONES.pending;

  const handleApprove = async () => {
    setBusy(true);
    await onApprove(edit.id);
    setBusy(false);
  };
  const handleReject = async () => {
    setBusy(true);
    await onReject(edit.id);
    setBusy(false);
  };

  return (
    <li
      data-testid={`listing-edit-${edit.id}`}
      className="border border-[var(--ld-border)] bg-white"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[var(--ld-ink)]/[0.02]"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-serif-display text-[16px] text-[var(--ld-ink)]">
              {edit.vendor_company || edit.vendor_email}
            </span>
            <span
              className={`text-[10px] tracking-[0.18em] uppercase border px-2 py-0.5 ${tone}`}
            >
              {edit.status}
            </span>
          </div>
          <div className="mt-1 text-[12px] text-[var(--ld-ink-muted)]">
            {keys.length} field{keys.length === 1 ? "" : "s"} · submitted{" "}
            {fmtDate(edit.submitted_at)}
          </div>
        </div>
        {open ? (
          <ChevronUp size={16} strokeWidth={1.4} className="text-[var(--ld-ink-muted)]" />
        ) : (
          <ChevronDown size={16} strokeWidth={1.4} className="text-[var(--ld-ink-muted)]" />
        )}
      </button>

      {open && (
        <div className="border-t border-[var(--ld-border)] px-5 pt-4 pb-5 space-y-3">
          {keys.map((k) => (
            <div key={k} className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-x-5 gap-y-1 text-[13px]">
              <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
                {FIELD_LABEL[k] || k}
              </div>
              <div>
                <span className="text-[var(--ld-ink-muted)] line-through mr-2">
                  {fmtValue(edit.previous?.[k])}
                </span>
                <span className="text-[var(--ld-ink)]">→</span>{" "}
                <span className="text-emerald-700 font-medium">
                  {fmtValue(edit.proposed?.[k])}
                </span>
              </div>
            </div>
          ))}

          {edit.review_note && (
            <p className="mt-3 text-[12px] italic text-[var(--ld-ink-muted)]">
              Admin note: {edit.review_note}
            </p>
          )}
          {edit.reviewed_at && (
            <p className="text-[11px] text-[var(--ld-ink-muted)]">
              Reviewed {fmtDate(edit.reviewed_at)} by {edit.reviewer_email}
            </p>
          )}

          {edit.status === "pending" ? (
            <div className="flex items-center justify-end gap-2 pt-3">
              <button
                data-testid={`listing-edit-reject-${edit.id}`}
                onClick={handleReject}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-rose-500/40 text-rose-700 px-3 py-2 hover:bg-rose-50 disabled:opacity-60"
              >
                <XIcon size={12} strokeWidth={1.6} />
                Reject
              </button>
              <button
                data-testid={`listing-edit-approve-${edit.id}`}
                onClick={handleApprove}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] px-3 py-2 hover:opacity-90 disabled:opacity-60"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={1.6} />}
                Approve & apply
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-end pt-3">
              <button
                data-testid={`listing-edit-delete-${edit.id}`}
                onClick={() => onDelete(edit.id)}
                className="text-[10px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] hover:text-rose-700"
              >
                Delete record
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

const ListingEditsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending | all | approved | rejected

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/listing-edits");
      setRows(r.data || []);
    } catch (e) {
      toast("Couldn't load listing edits", { duration: 2400 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const counts = useMemo(
    () => ({
      pending: rows.filter((r) => r.status === "pending").length,
      approved: rows.filter((r) => r.status === "approved").length,
      rejected: rows.filter((r) => r.status === "rejected").length,
    }),
    [rows]
  );

  const approve = async (id) => {
    try {
      await api.post(`/admin/listing-edits/${id}/approve`, {});
      toast("Edit applied — vendor notified", { duration: 2200 });
      await load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Couldn't approve", { duration: 2400 });
    }
  };

  const reject = async (id) => {
    const note = window.prompt(
      "Optional: add a short note explaining the rejection (sent to the vendor by email). Leave blank to skip.",
      ""
    );
    // null = cancel button → abort the whole action
    if (note === null) return;
    try {
      await api.post(`/admin/listing-edits/${id}/reject`, { note: note || null });
      toast("Edit rejected — vendor notified", { duration: 2200 });
      await load();
    } catch (e) {
      toast("Couldn't reject", { duration: 2400 });
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this edit record?")) return;
    try {
      await api.delete(`/admin/listing-edits/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (e) {
      toast("Couldn't delete", { duration: 2200 });
    }
  };

  return (
    <div data-testid="section-listing-edits">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
            Vendor self-edits
          </span>
          <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
            Listing Edits
            {counts.pending > 0 && (
              <span className="ml-3 inline-flex items-center text-[11px] tracking-[0.18em] uppercase bg-amber-100 text-amber-800 px-2.5 py-1 align-middle">
                {counts.pending} pending
              </span>
            )}
          </h2>
          <p className="mt-2 text-sm text-[var(--ld-ink-muted)] max-w-xl">
            Vendors can request changes to their pricing, contact details and
            bio. Review the diff, then approve to apply or reject to dismiss.
          </p>
        </div>
        <button
          data-testid="listing-edits-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          <RefreshCcw size={14} strokeWidth={1.6} />
          Refresh
        </button>
      </div>

      <div className="mt-8 flex items-center gap-1 border-b border-[var(--ld-border)]">
        {[
          ["pending", `Pending (${counts.pending})`],
          ["approved", `Approved (${counts.approved})`],
          ["rejected", `Rejected (${counts.rejected})`],
          ["all", "All"],
        ].map(([key, label]) => {
          const active = filter === key;
          return (
            <button
              key={key}
              data-testid={`listing-edits-filter-${key}`}
              onClick={() => setFilter(key)}
              className={`px-4 py-3 text-[11px] tracking-[0.22em] uppercase border-b-2 -mb-px transition-colors ${
                active
                  ? "border-[var(--ld-gold-deep)] text-[var(--ld-ink)]"
                  : "border-transparent text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {loading && (
          <div className="flex items-center gap-2 text-[var(--ld-ink-muted)]">
            <Loader2 size={14} className="animate-spin" />
            <span>Loading…</span>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="border border-[var(--ld-border)] p-10 text-center text-[var(--ld-ink-muted)] italic">
            {filter === "pending" ? (
              <>
                <Clock
                  size={20}
                  strokeWidth={1.4}
                  className="mx-auto mb-3 text-[var(--ld-gold-deep)]"
                />
                No pending edits — you&apos;re all caught up.
              </>
            ) : (
              "No edits in this bucket."
            )}
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="space-y-3" data-testid="listing-edits-list">
            {filtered.map((edit) => (
              <ListingEditRow
                key={edit.id}
                edit={edit}
                onApprove={approve}
                onReject={reject}
                onDelete={del}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ListingEditsPanel;
